import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import Button from "../atoms/Button";
import DotsLoader from "../atoms/DotsLoader";
import CategoryBadge from "../CategoryBadge";
import { h1, h3, h4, bodyLarge } from "../commonStyles/Fonts";
import { useQuery } from "../pages/SearchPage";
import { styled } from "../../stitches.config";
import Link from "../../components/Link";
import ProgressiveMedia from "../../components/common/ProgressiveMedia";

import { useStores, pushDataLayer } from "../../helpers"
import QuizAdslot from "./QuizAdslot";

import jsonToFormData from '../../helpers/jsonToFormData';

const QuizImagePlaceholder = {
    "hash": "e815e65c667737baf39823dbfda1b5b6",
    "media": {
        "type": "image",
        "format": "webp",
        "image_scheme": `https://${window.MEDIA_DOMAIN}/e815e65c667737baf39823dbfda1b5b6,%WIDTH%,%HEIGHT%,%CROP_FLAG%,%GRAYSCALE_FLAG%.webp`,
        "thumbnail_scheme": `https://${window.MEDIA_DOMAIN}/e815e65c667737baf39823dbfda1b5b6,%WIDTH%,%HEIGHT%,%CROP_FLAG%,%GRAYSCALE_FLAG%.webp`
    }
};

const Stages = ({ questionCount, active, answers }) => {
    const items = new Array(questionCount).fill(null);

    const getStageType = (index) => {
        if (answers[index]?.correct) return "good";
        if (answers[index]?.correct === false) return "wrong";
        if (index + 1 <= active) return "active";
    };
    return (
        <StagesWrapper>
            {items.map((_, index) => (
                <Stage type={getStageType(index)} />
            ))}
        </StagesWrapper>
    );
};

const FinalStep = ({ resetForm, answers, setAllAnswers, quiz_results }) => {
    const correctAnswers = answers.filter((item) => item.correct && item);
    const score = Math.round((correctAnswers.length / answers.length) * 100);

    return (
        <FinalWrapper>
            <FinalMessageWrapper>
                <p
                    className={h1()}
                // style={{ color: score >= 50 ? "#459B2B" : "#EA3A3E" }}
                >
                    Twój wynik to {correctAnswers.length}/{answers.length}
                </p>
                <FinalMessage className={h1()}>
                    {quiz_results?.map(
                        ({ min, max, text, button_text, button_link }) =>
                            correctAnswers.length >= min && correctAnswers.length <= max &&
                            <span>{text}</span>
                    )}
                </FinalMessage>
            </FinalMessageWrapper>
            <div>
                <Button
                    type="secondary"
                    starPosition="none"
                    text="Spróbuj jeszcze raz"
                    onClick={resetForm}
                />
                <Button
                    type="primary"
                    starPosition="none"
                    text="Pokaż odpowiedzi"
                    onClick={() => setAllAnswers(true)}
                />
                {quiz_results?.map(
                    ({ min, max, button_text, button_link }) =>
                        correctAnswers.length >= min && correctAnswers.length <= max && button_link &&
                        <Link to={button_link}>{button_text}</Link>
                )}
            </div>
        </FinalWrapper>
    );
};

const QuizTest = ({ data, element }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const urlParams = useParams();

    const { questions, quiz_results } = data;

    const [isLoading, setLoading] = React.useState(false);
    const [currentStep, setCurrentStep] = React.useState(0);
    const [selectedOption, setSelectedOption] = React.useState("");
    const [currentAnswerId, setCurrentAnswerId] = React.useState("");
    const [currentQuestionId, setCurrentQuestionId] = React.useState("");
    const [answers, setAnswers] = React.useState([]);
    const [isAllAnswers, setAllAnswers] = React.useState(false);
    const [isQuizVisable, setIsQuizVisible] = React.useState(false);
    const [isPageViewSent, setIsPageViewSent] = React.useState(false);

    const [isQuizImageReady, setQuizImageReady] = useState(false);
    useEffect(() => setQuizImageReady(false), [currentStep, isAllAnswers]);

    const imagePlaceholder = useMemo(() => data?.placeholder?.media ?? QuizImagePlaceholder, [data]);
    const showImage = useMemo(() => questions.reduce((s, q) => s || !!q?.picture, false), [data]);

    const query = useQuery();

    useEffect(() => {
        const queryQuestion = element?.quiz_question ?? query.get(`q${element.quiz_id}`);

        const queryNumber = Number(queryQuestion);
        setAnswers(answers.filter((el, index) => index < queryNumber && el));
        setCurrentStep(queryNumber);
    }, [query, element]);

    const resetForm = () => {
        goToNextQuestion(0);
        setCurrentStep(0);
        setSelectedOption("");
        setCurrentAnswerId("");
        setCurrentQuestionId("");
        setAnswers([]);
        setAllAnswers(false);
        setLoading(false);
    };

    useEffect(() => {
        setSelectedOption("");
        setLoading(false);
    }, [currentStep]);

    const goToNextQuestion = (step = undefined) => {
        if (step && step > questions.length || !step && currentStep + 1 > questions.length)
            return;

        const questionId = step ?? currentStep + 1;

        if (element?.handleGoToNextQuestion) {
            element.handleGoToNextQuestion(questionId);
        } else {
            navigate({
                pathname: location.pathname,
                search: `?q${element.quiz_id}=${questionId}`,
            }, {
                state: { soft: true }
            });
        }
    };

    const onChangeRadio = (e, isCorrect, answer_id) => {
        setLoading(true);
        setSelectedOption(e.target.value);
        setAnswers((prev) => [
            ...prev,
            { option: e.target.value, correct: isCorrect || false, answerNumber: currentStep },
        ]);

        if (element?.handleGoToNextQuestion)
            goToNextQuestion();
        else
            setTimeout(() => goToNextQuestion(), 1500);

        setCurrentQuestionId(questions[currentStep]?.question_id);
        setCurrentAnswerId(answer_id);
    };

    const { ArticleStore, UIStore } = useStores();


    // commercial break
    const [commercialActive, setCommercialActive] = React.useState(false);

    const handleQuizAdslotClose = useCallback(() => {
        goToNextQuestion();
    }, [currentStep]);

    useEffect(() => {
        if (questions[currentStep]?.type === "adslot") {
            setCommercialActive(true);
        } else {
            setCommercialActive(false);
        }
    }, [currentStep, data]);

    // adding virtual page view

    const dataLayerObject = {
        'event': 'virtualPageview',
        'virtualPageURL': ArticleStore.article.uri + `?q${element.quiz_id}=${(currentStep + 1) == questions.length ? "result" : (currentStep + 1)}`,
        'virtualPageTitle': ArticleStore.article.seo_title,
        'pageType': 'Artykuł Quizowy',
    }

    var randomId = Math.floor(Math.random() * Date.now()) + Date.now();
    const [pageViewId, setPageViewId] = useState(randomId)
    const timestamp = Date.now();

    useEffect(() => {
        const fetchData = () => {

            navigator.sendBeacon(`${window.API_HOST}/api/quiz/user-answers/${element.quiz_id}`, jsonToFormData({
                pageViewId: pageViewId,
                timestamp: timestamp,
                answerId: currentAnswerId,
                questionId: currentQuestionId,
            }))
        }

        if (currentQuestionId !== "") {
            fetchData()
        }

    }, [currentQuestionId]);

    // pageView on scroll 

    const myRef = useRef()

    useEffect(() => {
        if (!myRef.current)
            return;

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            setIsQuizVisible(entry.isIntersecting)
        });
        observer.observe(myRef.current);
    }, [])

    useEffect(() => {
        const fetchData = () => {

            navigator.sendBeacon(`${window.API_HOST}/api/quiz/page-view/${element.quiz_id}`, jsonToFormData({
                pageViewId: pageViewId,
                timestamp: timestamp,
            }))
        }

        if (isQuizVisable && !isPageViewSent) {
            fetchData()
            setIsPageViewSent(true)
        }
    }, [isQuizVisable]);


    if (element.quiz_type === "non-inline")
        return (
            <QuizButtonWrapper>
                <Link to={`${urlParams.slug}/quiz/${element.quiz_id}`}>
                    {data.name}
                </Link>
            </QuizButtonWrapper>
        )

    //console.log("step:", currentStep, "ql:", questions.length)
    return (
        <Wrapper ref={myRef} commercialActive={commercialActive} showImage={showImage}>
            <ImageWrapper keepAspectRatio={!commercialActive} showImage={showImage} imageReady={isQuizImageReady}>
                {commercialActive ? (
                    <CommercialWrapper showImage={showImage}>
                        <QuizAdslot
                            name={questions[currentStep]?.adSlot_name}
                            onClose={handleQuizAdslotClose}
                        />
                    </CommercialWrapper>
                ) : showImage && (
                    questions[currentStep]?.picture ? (
                        <ProgressiveMedia
                            mediaProperties={questions[currentStep].picture.media} src={questions[currentStep].picture.img} aspectRatio={1.5}
                            onLoadFinished={() => setQuizImageReady(true)}
                        />
                    ) : (
                        <ProgressiveMedia mediaProperties={imagePlaceholder} aspectRatio={1.5} />
                    )
                )}
            </ImageWrapper>
            <CategoryBadge text="Quiz" />
            <Stages
                questionCount={element.question_count}
                active={!questions[currentStep]?.type === "adslot" && currentStep + 1}
                answers={answers}
            />
            {!isAllAnswers && currentStep !== questions.length ? (
                <>
                    <h3>
                        {!commercialActive ? (
                            <>
                                <span>
                                    {questions[currentStep]?.question_number} / {element.question_count}
                                </span>{" "}
                                {questions[currentStep]?.question_text}
                            </>
                        ) : (
                            "Przerwa na reklamę, za chwilę zobaczysz kolejne pytanie."
                        )}
                    </h3>
                    <PictureDescription>
                        {commercialActive ? "Reklama" : questions[currentStep]?.picture_text}
                    </PictureDescription>
                    <OptionsWrapper commercialActive={commercialActive}>
                        {questions[commercialActive ? currentStep - 1 : currentStep].answers?.map(
                            ({ answer_text, is_correct, answer_id }) =>
                                answer_text && (
                                    <Option key={answer_text} type={selectedOption && (is_correct ? "good" : selectedOption === answer_text ? "wrong" : null)}>
                                        <input
                                            type="radio"
                                            id={answer_text}
                                            value={answer_text}
                                            checked={selectedOption === answer_text}
                                            onChange={(e) => onChangeRadio(e, is_correct, answer_id)}
                                            onClick={() => {
                                                if (data.quiz_type !== "page") {
                                                    pushDataLayer(dataLayerObject);

                                                    UIStore.trackPageView({
                                                        page: dataLayerObject.virtualPageURL,
                                                        title: '- ' + dataLayerObject.virtualPageTitle
                                                    });
                                                }
                                            }}

                                        />
                                        <label className={bodyLarge()} htmlFor={answer_text}>
                                            {answer_text}
                                        </label>
                                        {isLoading && selectedOption === answer_text && (
                                            <LoaderWrapper>
                                                <DotsLoader />
                                            </LoaderWrapper>
                                        )}
                                    </Option>
                                )
                        )}
                    </OptionsWrapper>
                </>
            ) : (
                <FinalStep
                    resetForm={resetForm}
                    answers={answers}
                    setAllAnswers={setAllAnswers}
                    quiz_results={quiz_results}
                />
            )
            }

            <AllAnswers>
                {isAllAnswers && questions.map((el, index) => (
                    <>
                        {questions[index]?.type === "adslot" ?
                            null
                            :
                            <>
                                <h3 className={h3()}>
                                    <span>
                                        {questions[index]?.question_number} / {element.question_count}
                                    </span>{" "}
                                    {questions[index]?.question_text}
                                </h3>
                                {el.answers?.map(({ answer_text, is_correct }) => {
                                    if (!answer_text)
                                        return null;

                                    const answer = answers.find(a => { return a.option === answer_text && a.answerNumber === index });

                                    return (
                                        <Option type={is_correct ? "good" : answer && "wrong"}>
                                            <input
                                                type="radio"
                                                id={answer_text}
                                                value={answer_text}
                                                checked={!!answer}
                                            />
                                            <label className={bodyLarge()} htmlFor={answer_text}>
                                                {answer_text}
                                            </label>
                                        </Option>
                                    );
                                })}
                            </>
                        }
                    </>
                ))}
            </AllAnswers>
        </Wrapper>
    );
};

const PictureDescription = styled("p", {
    margin: "4px 0 16px 0",
    fontSize: "12px",
    color: "$grayscale90",
    fontStyle: "italic",
    lineHeight: "16px",
});

const Option = styled("div", {
    border: "1px solid $grayscale25",
    padding: "0 12px",
    marginBottom: "16px",
    position: "relative",
    display: "flex",
    alignItems: "center",

    label: {
        padding: "12px 0",
        paddingLeft: "16px",
        display: "inline-block",
        width: "100%",
        cursor: "pointer",
    },

    input: {
        width: "12px",
    },

    "input[type='radio']:after": {
        width: "16px",
        height: "16px",
        borderRadius: "16px",
        top: "-1px",
        left: "-1px",
        position: "relative",
        backgroundColor: "$grayscale0",
        content: "",
        visibility: "visible",
        border: "1px solid $grayscale25",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },

    "input[type='radio']:checked:after": {
        width: "16px",
        height: "16px",
        borderRadius: "16px",
        top: "-1px",
        left: "-1px",
        position: "relative",
        backgroundColor: "$natematPrimary",
        content: "✓",
        color: "$grayscale0",
        visibility: "visible",
        border: "1px solid $grayscale25",
        fontSize: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },

    variants: {
        type: {
            good: {
                borderColor: "#459B2B !important",
            },
            wrong: {
                borderColor: "#EA3A3E !important",
            },
        },
    },
});

const OptionsWrapper = styled("div", {
    position: "relative",
    "&::before": {
        content: "",
        position: "absolute",
        inset: 0,
        zIndex: 2,
        display: 'none'
    },
    variants: {
        commercialActive: {
            true: {
                "&::before": {
                    display: "block"
                },
                filter: "blur(2px)"
            },
            false: {}
        }
    }
});

const StagesWrapper = styled("div", {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "5px",
    marginTop: "16px",

    div: {
        background: "$grayscale50",
        width: "100%",
        height: "2px",
    },
});

const CommercialWrapper = styled("div", {
    padding: "1rem 0",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    variants: {
        showImage: {
            true: {},
            false: {
                position: 'absolute',
                inset: '4rem 0 0'
            }
        },
    },
});

const ImageWrapper = styled("div", {
    variants: {
        imageReady: {
            true: {},
            false: {}
        },
        keepAspectRatio: {
            true: {},
            false: {}
        },
        showImage: {
            true: {
                width: "100%",
                '@bp4': {
                    aspectRatio: "6 / 4",
                },
            },
            false: {}
        }
    },
    compoundVariants: [
        {
            keepAspectRatio: true,
            showImage: true,
            css: {
                aspectRatio: "6 / 4"
            }
        },
        {
            showImage: true,
            imageReady: true,
            css: {
                [`& > div`]: {
                    background: 'none',
                    '& img': {
                        objectFit: 'contain',
                        objectPosition: 'center'
                    }
                }
            }
        }
    ]
});

const Stage = styled("div", {
    variants: {
        type: {
            active: {
                background: "$grayscale75 !important",
            },
            good: {
                background: "#459B2B !important",
            },
            wrong: {
                background: "#EA3A3E !important",
            },
        },
    },
});

const LoaderWrapper = styled("div", {
    position: "absolute",
    top: "10px",
    right: "20px",
});

const FinalWrapper = styled("div", {
    padding: "50px 0 20px 0",
    textAlign: "center",
    position: "relative",
    div: {
        display: "flex",
        justifyContent: "center",
        gap: "10px",
        flexDirection: "column",
        alignItems: "center",
        "@bp4": {
            flexDirection: "row",
        }
    },
    a: {
        padding: "5px 12px 7px 12px",
        color: "$grayscale0Fixed !important",
        backgroundColor: "$primary",
        border: "2px solid $primary",
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: 600,

        "&:hover": {
            backgroundColor: "$primary120",
            border: "2px solid $primary120",
            color: "$grayscale0Fixed !important",
            textDecoration: "none",
        },

        "&:visited": {
            color: "$grayscale0Fixed !important",
        }
    }
});

const FinalMessageWrapper = styled("div", {
    flexDirection: "column !important",
    gap: 0,
    fontSize: "32px",
    "@bp4": {
        position: "relative",
        fontSize: "40px",
    },

    p: {
        margin: 0,
    }
});

const FinalMessage = styled("p", {
    fontSize: "20px",
    margin: "0 0 40px 0 !important",
    fontWeight: 500,
    "@bp4": {
        fontSize: "24px",
        margin: 0
    },
    span: {
        color: "$grayscale90 !important",
    }
});

const AllAnswers = styled("div", {});

const QuizButtonWrapper = styled('div', {
    "a": {
        width: "100%",
        margin: "0 auto 18px auto",
        color: "$grayscale0Fixed !important",
        backgroundColor: "$primary",
        border: "2px solid $primary",
        cursor: "pointer",
        padding: "7px 16px 9px 16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
            backgroundColor: "$primary120",
            border: "2px solid $primary120",
        },
    }
});

const Wrapper = styled("div", {
    padding: "24px 16px",
    background: "$grayscale0",
    position: "relative",

    h3: {
        margin: "16px 0",
    },

    span: {
        color: "$grayscale50",
    },

    "@bp4": {
        padding: "32px",
    },
    variants: {
        showImage: { true: {}, false: {} },
        commercialActive: { true: {}, false: {} },
    },
    compoundVariants: [{
        showImage: false,
        commercialActive: true,
        css: {
            [`& ${PictureDescription}, & ${OptionsWrapper}, & ${StagesWrapper}`]: {
                visibility: "hidden"
            },
            [`& h3`]: {
                transform: 'translateY(-24px)'
            },
            [`& ${Option}`]: {
                'input[type="radio"]': {
                    display: 'none'
                }
            }
        }
    }]
});

export default QuizTest;
